"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import {
	exchangeCodeForTokens,
	setCognitoSessionFromTokens,
} from "@/services/cognito";
import { getOrigin } from "@/services/cognito";
import { useOnboarding } from "@/contexts/OnboardingContext";

export default function OAuthCallbackHandler({
	checkAuth,
	setIsChecking,
}: {
	checkAuth: () => void;
	setIsChecking: (isChecking: boolean) => void;
}) {
	const router = useRouter();
	const [isProcessing, setIsProcessing] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [tokensSet, setTokensSet] = useState(false);
	const hasProcessedRef = useRef(false); // Prevent multiple executions
	const {
		isOnboarded,
		isLoading: onboardingLoading,
		userNotFound,
		refreshProfile,
	} = useOnboarding();

	useEffect(() => {
		// Prevent multiple executions (React strict mode)
		if (hasProcessedRef.current) {
			return;
		}

		const handleOAuthCallback = async () => {
			console.log("Handle auth callback");
			// Mark as processing to prevent duplicate calls
			hasProcessedRef.current = true;
			// Get URL parameters from window.location (works in client component)
			const urlParams = new URLSearchParams(window.location.search);
			const code = urlParams.get("code");
			const errorParam = urlParams.get("error");

			// Check for OAuth error
			if (errorParam) {
				const errorDescription = urlParams.get("error_description");
				setError(
					errorDescription ||
						errorParam ||
						"Authentication failed. Please try again."
				);
				setIsProcessing(false);
				// Redirect to login after a delay
				setTimeout(() => {
					router.push("/login");
				}, 3000);
				return;
			}

			// If no code, this isn't an OAuth callback
			if (!code) {
				console.log(
					"OAuth callback - No code, setting isChecking to false"
				);
				await checkAuth();
				setIsChecking(false);
				setIsProcessing(false);
				return;
			}

			try {
				// Get the redirect URI (should match what was used in signInWithProvider)
				const origin = getOrigin();
				const redirectUri = `${origin}/login`;

				console.log("OAuth callback - Origin:", origin);
				console.log("OAuth callback - Redirect URI:", redirectUri);
				console.log("OAuth callback - Code present:", !!code);

				// Exchange authorization code for tokens
				const tokenData = await exchangeCodeForTokens(
					code,
					redirectUri
				);

				// Set the Cognito session with the tokens
				await setCognitoSessionFromTokens(
					tokenData.id_token,
					tokenData.access_token,
					tokenData.refresh_token
				);

				// Clear the URL parameters
				window.history.replaceState({}, document.title, "/login");

				// Refresh profile and redirect to auth redirect page
				await refreshProfile();
				// Redirect to auth redirect page which will handle all redirect logic
				router.push("/auth/redirect");
				setIsChecking(false);
				setIsProcessing(false);
			} catch (err) {
				console.error("OAuth callback error:", err);
				setError(
					err instanceof Error
						? err.message
						: "Failed to complete authentication. Please try again."
				);
				setIsProcessing(false);
				// Redirect to login after a delay
				setTimeout(() => {
					router.push("/login");
				}, 3000);
				await checkAuth();
				setIsChecking(false);
			}
		};

		handleOAuthCallback();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Only run once on mount

	if (error) {
		return (
			<div className="min-h-screen bg-[#16251c] flex items-center justify-center">
				<div className="text-center">
					<div className="w-12 h-12 border-4 border-[#A7FF5E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
					<p className="text-[#edffde] text-sm">
						Error in authentication...
					</p>
					<p className="text-[#8c99a7] text-xs">
						Please try again or contact support.
					</p>
				</div>
			</div>
		);
	}

	if (isProcessing) {
		return (
			<div className="min-h-screen bg-[#16251c] flex items-center justify-center">
				<div className="text-center">
					<div className="w-12 h-12 border-4 border-[#A7FF5E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
					<p className="text-[#edffde] text-sm">
						Completing authentication...
					</p>
				</div>
			</div>
		);
	}

	return null;
}
